import { useEffect, useMemo, useState, type ReactNode, type RefObject } from 'react';

import { cn } from '@/shared/utils/cn';

import type { SummaryRow } from './form-display';

const COUNTDOWN_UNITS = [
  ['days', 'DÍAS'],
  ['hours', 'HORAS'],
  ['minutes', 'MIN.'],
  ['seconds', 'SEG.'],
] as const;

type CountdownRemaining = Record<(typeof COUNTDOWN_UNITS)[number][0], number>;

export function FormHeader({
  title,
  description,
  align = 'left',
}: {
  title: string;
  description?: ReactNode;
  align?: 'left' | 'center';
}) {
  return (
    <div className={cn('pb-5 sm:pb-6', align === 'center' && 'text-center')}>
      <h2 className="text-adipa-text-primary text-xl font-semibold leading-tight">{title}</h2>
      {description && (
        <div className="text-adipa-text-secondary mt-1 text-sm leading-6">{description}</div>
      )}
    </div>
  );
}

/**
 * Cabecera de un formulario por pasos: barra de progreso, "Paso X de Y" y el
 * título del paso actual. El título recibe el foco al cambiar de paso para que
 * un lector de pantalla anuncie dónde quedó el usuario.
 */
export function FormSteps({
  steps,
  currentIndex,
  headingId,
  headingRef,
}: {
  steps: { id: string; title: string; description?: string }[];
  currentIndex: number;
  headingId: string;
  headingRef?: RefObject<HTMLHeadingElement | null>;
}) {
  const current = steps[currentIndex];

  if (!current) return null;

  return (
    <div className="mb-5 sm:mb-6">
      <p className="text-adipa-text-secondary text-xs font-semibold uppercase tracking-wide">
        {`Paso ${currentIndex + 1} de ${steps.length}`}
      </p>
      <ol className="mt-2 flex items-center gap-2">
        {steps.map((step, index) => (
          <li key={step.id} className="flex-1" aria-current={index === currentIndex ? 'step' : undefined}>
            <span className="sr-only">
              {`Paso ${index + 1} de ${steps.length}: ${step.title}`}
              {index < currentIndex ? ' (completado)' : ''}
            </span>
            <span
              aria-hidden="true"
              className={cn(
                'block h-1 rounded-full transition-colors',
                index <= currentIndex ? 'bg-adipa-primary' : 'bg-adipa-border',
              )}
            />
          </li>
        ))}
      </ol>
      <div className="mt-5">
        <h3
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="text-adipa-text-primary rounded-adipa-sm text-base font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-adipa-primary focus-visible:ring-offset-2"
        >
          {current.title}
        </h3>
        {current.description && (
          <p className="text-adipa-text-secondary mt-1 text-sm leading-6">{current.description}</p>
        )}
      </div>
    </div>
  );
}

export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="@lg:grid-cols-2 grid grid-cols-1 gap-x-4 gap-y-5">{children}</div>;
}

export function parseCountdownDeadline(deadline?: string) {
  if (!deadline) return undefined;

  const timestamp = Date.parse(deadline);
  return Number.isNaN(timestamp) ? undefined : timestamp;
}

export function FormCountdownBanner({ deadline, text }: { deadline?: string; text?: string }) {
  const remaining = useCountdown(deadline);

  if (!remaining) return null;

  const bannerText = text ?? 'La fecha límite se acerca';

  return (
    <aside
      className="rounded-t-adipa-lg bg-adipa-secondary text-adipa-text-inverse -mx-5 -mt-5 mb-5 px-5 py-3 sm:-mx-6 sm:-mt-6 sm:mb-6 sm:px-6"
      aria-label={`Cuenta atrás: ${bannerText}`}
      aria-live="polite"
    >
      <div className="@lg:flex-row @lg:justify-between flex flex-col items-center gap-3">
        <p className="@lg:flex-1 @lg:text-left @lg:text-lg min-w-0 text-center text-sm font-semibold leading-5">
          {bannerText}
        </p>
        <div className="@lg:shrink-0 grid grid-cols-4 gap-2">
          {COUNTDOWN_UNITS.map(([unit, label]) => (
            <div
              key={unit}
              className="min-w-11 text-center"
              aria-label={`${remaining[unit]} ${label.toLowerCase()}`}
            >
              <span className="rounded-adipa-md bg-adipa-surface text-adipa-text-primary block px-2 py-1 text-base font-semibold leading-6">
                {formatCountdownValue(remaining[unit])}
              </span>
              <span className="text-adipa-console-xxs text-adipa-text-inverse mt-1 block font-semibold uppercase leading-3">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

export function FormFooter({
  children,
  requiredFieldsLabel,
  compactTopSpacing,
  sticky,
}: {
  children: ReactNode;
  requiredFieldsLabel?: string;
  compactTopSpacing?: boolean;
  /** Deja los CTA pegados abajo cuando el formulario vive en un contenedor con scroll. */
  sticky?: boolean;
}) {
  const shouldHighlightRequiredAsterisk = requiredFieldsLabel?.includes('*') ?? false;
  const requiredLabelParts = requiredFieldsLabel?.split('*');
  const requiredLabel = requiredFieldsLabel ? (
    shouldHighlightRequiredAsterisk && requiredLabelParts ? (
      <p className="text-adipa-text-secondary text-xs leading-5" aria-label={requiredFieldsLabel}>
        {requiredLabelParts[0]}
        <span aria-hidden="true" className="text-adipa-error font-semibold">
          *
        </span>
        {requiredLabelParts.slice(1).join('*')}
      </p>
    ) : (
      <p className="text-adipa-text-secondary text-xs leading-5">{requiredFieldsLabel}</p>
    )
  ) : null;

  return (
    <div
      // El host mide esta barra para colocar el indicador de scroll encima.
      data-sticky-footer={sticky ? '' : undefined}
      className={cn(
        sticky ? 'space-y-0' : 'space-y-3',
        compactTopSpacing ? 'mt-4' : 'mt-5 sm:mt-6',
        // Los CTA quedan siempre a la vista y el degradado avisa que hay más
        // formulario arriba del borde.
        sticky && [
          'bg-adipa-surface border-adipa-border sticky bottom-0 z-10 border-t pb-3 pt-3',
          'before:bg-linear-to-t before:from-adipa-surface before:pointer-events-none',
          'before:absolute before:inset-x-0 before:-top-6 before:h-6 before:to-transparent',
        ],
      )}
    >
      {sticky ? (
        // Una sola fila: la nota ocupa el espacio libre a la izquierda y los CTA
        // quedan a la derecha, así la barra pegada no roba alto al formulario.
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {requiredLabel}
          {children}
        </div>
      ) : (
        <>
          {requiredLabel}
          {children}
        </>
      )}
    </div>
  );
}

export function FieldInfo({ text }: { text?: string }) {
  if (!text) return null;
  return <p className="text-adipa-text-secondary text-xs leading-5">{text}</p>;
}

export function ProfileGuidance({ profileUrl }: { profileUrl?: string }) {
  return (
    <div className="text-adipa-text-secondary text-sm leading-6">
      {profileUrl ? (
        <p>
          ¿Algún dato está desactualizado?{' '}
          <a
            href={profileUrl}
            className="text-adipa-primary font-medium underline underline-offset-4 hover:no-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Edita tus datos en tu perfil
          </a>{' '}
          antes de continuar.
        </p>
      ) : (
        <p>¿Algún dato está desactualizado? Actualízalo desde tu perfil antes de continuar.</p>
      )}
    </div>
  );
}

export function LoggedInDescription({
  message,
  firstName,
}: {
  message: string;
  firstName?: string;
}) {
  if (!firstName || !message.startsWith(`${firstName}, `)) {
    return <p>{message}</p>;
  }

  return (
    <p>
      <span className="text-adipa-primary font-semibold">{firstName}</span>
      {message.slice(firstName.length)}
    </p>
  );
}

export function AccountSummary({ rows }: { rows: SummaryRow[] }) {
  return (
    <div className="mb-5">
      <section>
        <h3 className="text-adipa-text-primary text-base font-semibold">Datos que enviaremos</h3>

        <dl className="mt-3 grid gap-2.5">
          {rows.map((row) => (
            <div
              key={row.key}
              className="rounded-adipa-md border-adipa-border bg-adipa-surface-muted @lg:grid-cols-2 @lg:items-center @lg:gap-4 grid min-h-10 gap-1 border px-3 py-2 text-sm"
            >
              <dt className="text-adipa-text-secondary text-xs font-medium uppercase tracking-wide">
                {row.label}
              </dt>
              <dd className="text-adipa-text-primary @lg:text-right font-semibold">{row.value}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

function useCountdown(deadline?: string) {
  const targetTimestamp = useMemo(() => parseCountdownDeadline(deadline), [deadline]);
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    if (!targetTimestamp) return undefined;

    setNow(Date.now());
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, [targetTimestamp]);

  return targetTimestamp && now !== null ? getCountdownRemaining(targetTimestamp, now) : undefined;
}

function getCountdownRemaining(deadline: number, now: number): CountdownRemaining {
  const totalSeconds = Math.max(0, Math.floor((deadline - now) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}

function formatCountdownValue(value: number) {
  return String(value).padStart(2, '0');
}
